import styled from '@emotion/styled';
import { css, keyframes, SerializedStyles } from '@emotion/react';
import { withConfig } from 'src/withConfig';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';

interface WrapperType {
    computedHeight: number;
}

export const progress = keyframes`
    0% {
        transform: translateX(-50%);
    }
    100% {
        transform: translateX(150%);
    }
`;

type SizeType = 'small' | 'header' | 'scoreboard' | 'sportsFilters' | undefined;

interface LoaderType {
    size?: SizeType;
}

const setPrimaryHederHeight = (type: SizeType): string => {
    switch (type){
        case 'small':
            return '80px';
        case 'header':
            return '40px';
        case 'scoreboard':
            return '130px';
        case 'sportsFilters':
            return '44px';
        default:
            return '88px';
    }
};

export const PrimaryLoaderContainer = withConfig(theme => styled('div', { label: 'PrimaryLoaderContainer' })`
    position: relative;
    overflow: hidden;
    &::after {
        animation: ${progress} 1s linear infinite;
        background-image: ${`linear-gradient(to right,
            ${theme.star.skeletonLoader.gradient.bgColorOctonary} 0%,
            ${theme.star.skeletonLoader.gradient.bgColorQuaternary} 15%,
            ${theme.star.skeletonLoader.gradient.bgColor} 50%,
            ${theme.star.skeletonLoader.gradient.bgColorQuaternary} 95%,
            ${theme.star.skeletonLoader.gradient.bgColorOctonary} 100%)
            `};
        content: '';
        height: 100%;
        left: -100%;
        position: absolute;
        top: 0;
        width: 100%;
        background-size: 300%;
    }
`);

const SilkLoaderContainer = withConfig(theme => styled('div', { label: 'SilkLoaderContainer' })`
    position: relative;
    overflow: hidden;
    &::after {
        animation: ${progress} 1s linear infinite;
        background-image: ${`linear-gradient(to right,
            ${theme.star.skeletonLoader.gradient.bgColorOctonary} 0%,
            ${theme.star.skeletonLoader.gradient.bgColorQuaternary} 15%,
            ${theme.star.skeletonLoader.gradient.bgColor} 50%,
            ${theme.star.skeletonLoader.gradient.bgColorQuaternary} 95%,
            ${theme.star.skeletonLoader.gradient.bgColorOctonary} 100%)
        `};
        content: '';
        height: 100%;
        left: -100%;
        position: absolute;
        top: 0;
        width: 100%;
        background-size: 250%;
        border-radius: 50%;
    }
`);

export const CasinoLoaderContainer = withConfig(theme => styled('div', { label: 'CasinoLoaderContainer' })`
    position: relative;
    overflow: hidden;
    &::after {
        animation: ${progress} 1.2s linear infinite;
        background-image: ${`linear-gradient(to right,
            ${theme.star.skeletonLoader.gradient.bgColorUndenary} 0%,
            ${theme.star.skeletonLoader.gradient.bgColorSenary} 30%,
            ${theme.star.skeletonLoader.gradient.bgColorSeptenary} 50%,
            ${theme.star.skeletonLoader.gradient.bgColorSenary} 70%,
            ${theme.star.skeletonLoader.gradient.bgColorUndenary} 100%)`};
        content: '';
        height: 100%;
        left: -100%;
        position: absolute;
        top: 0;
        width: 100%;
        background-size: 500%;
    }
`);

interface WrapperPropsType {
    variant: DefaultComponentVariantType | null;
}

export const setWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            border-radius: ${theme.star.radius.medium};
            overflow: hidden;
        `;
    }
    return css`
        &::before {
            background-color: ${theme.star.casino.gameInfo.bgColorTertiary};
            bottom: 20%;
            top: 20%;
        }
    `;
};


export const Wrapper = withConfig(theme => styled('div', { label: 'Wrapper' })<WrapperPropsType>`
    ${({ variant }): SerializedStyles => setWrapper(variant, theme)}
    display: flex;
    justify-content: space-between;
    background-color: ${theme.star.skeletonLoader.placeholder.bgColor};
`);

interface PlacehodlerWrapperPropsType {
    variant: DefaultComponentVariantType | null;
}

const setPlaceholderWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            margin: 8px 0;
            @media ${theme.star.mediaQuery.tablet} {
                margin: 8px;
            }
        `;
    }
    return css``;
};

export const PlacehodlerWrapper = withConfig(theme => styled('div', { label: 'PlacehodlerWrapper' })<PlacehodlerWrapperPropsType>`
    background-color: ${theme.star.skeletonLoader.placeholder.bgColor};
    ${({ variant }): SerializedStyles => setPlaceholderWrapper(variant, theme)}
`);

interface EventsTableLoaderPropsType extends LoaderType {
    variant: DefaultComponentVariantType | null;
}

export const PrimaryHeaderLoader = withConfig(theme => styled('div', { label: 'PrimaryHeaderLoader' })<EventsTableLoaderPropsType>`
    background-color: ${theme.star.skeletonLoader.placeholder.bgColorQuaternary};
    height: ${({ size }): string => setPrimaryHederHeight(size)};
    width: 100%;
    border-radius: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? theme.star.radius.small : '0'}
`);


const setEventsTableSportLoaderWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            background-color: ${theme.star.eventsTableColors.bgColor};
            margin: 0 0 8px;
            padding: ${theme.star.padding.medium};
            display: flex;
            flex-flow: column nowrap;
            gap: ${theme.star.gap.medium};
            @media ${theme.star.mediaQuery.tablet} {
                margin: 0 8px 8px;
                border-radius: ${theme.star.radius.medium};
            }
        `;
    }
    return css``;
};

export const EventsTableSportLoaderWrapper = withConfig(theme => styled('div', { label: 'EventsTableSportLoaderWrapper' })<EventsTableLoaderPropsType>`
    ${({ variant }): SerializedStyles => setEventsTableSportLoaderWrapper(variant, theme)}
`);

const setEventsTableMarketsLoaderWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            background-color: ${theme.star.eventsTableColors.bgColor};
            padding: ${theme.star.padding.medium};
            display: flex;
            flex-flow: column nowrap;
            gap: ${theme.star.gap.medium};
            @media ${theme.star.mediaQuery.tablet} {
                border-radius: 0 0 ${theme.star.radius.medium} ${theme.star.radius.medium};
                margin: 0 8px;
            }
        `;
    }
    return css``;
};

export const EventsTableMarketsLoaderWrapper = withConfig(theme => styled('div', { label: 'EventsTableMarketsLoaderWrapper' })<EventsTableLoaderPropsType>`
    ${({ variant }): SerializedStyles => setEventsTableMarketsLoaderWrapper(variant, theme)}
`);

export const MatchCardLoaderWrapper = withConfig(theme => styled('div', { label: 'MatchCardLoaderWrapper' })`
    display: flex;
    flex-flow: column nowrap;
    gap: ${theme.star.gap.medium};
    margin-top: 8px;
`);

const setEventsCardLoaderWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            overflow: hidden;
            margin: 8px 0;
            @media ${theme.star.mediaQuery.tablet} {
                margin: 8px;
                border-radius: ${theme.star.radius.medium};
            }
        `;
    }
    return css``;
};

export const EventsCardLoaderWrapper = withConfig(theme => styled('div', { label: 'EventsCardLoaderWrapper' })<EventsTableLoaderPropsType>`
    ${({ variant }): SerializedStyles => setEventsCardLoaderWrapper(variant, theme)}
`);

const setEventsLoaderWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            display: flex;
            flex-flow: column nowrap;
            gap: ${theme.star.gap.small};
        `;
    }
    return css``;
};

export const EventsLoaderWrapper = withConfig(theme => styled('div', { label: 'EventsLoaderWrapper' })<EventsTableLoaderPropsType>`
    ${({ variant }): SerializedStyles => setEventsLoaderWrapper(variant, theme)}
`);

const setSecondaryHeaderLoader = (variant: DefaultComponentVariantType | null, theme: EmotionTheme, size: SizeType): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            background-color: ${size === 'small' ? theme.star.skeletonLoader.placeholder.bgColorQuinary : theme.star.skeletonLoader.placeholder.bgColorQuaternary};
            height: ${size === 'small' ? '32px' : '36px'};
            border-radius: ${theme.star.radius.small};
        `;
    }
    return css`
        background-color: ${theme.star.skeletonLoader.placeholder.bgColorQuinary};
        height: ${size === 'small' ? '24px' : '44px'};
    `;
};

export const SecondaryHeaderLoader = withConfig(theme => styled('div', { label: 'SecondaryHeaderLoader' })<EventsTableLoaderPropsType>`
    ${({ variant, size }): SerializedStyles => setSecondaryHeaderLoader(variant, theme, size)}
    width: 100%;
`);

export const QuaternaryHeaderLoader = withConfig(theme => styled('div', { label: 'QuaternaryHeaderLoader' })`
    background-color: ${theme.star.skeletonLoader.placeholder.bgColorSecondary};
    height: 24px;
    width: 100%;
`);

export const EventsLoader = styled('div', { label: 'EventsLoader' })`
    display: flex;
    flex-direction: column;
    padding: 0 8px;
`;

const setEventRowLoader = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            height: 64px;
            overflow: hidden;
        `;
    }
    return css`
        height: 56px;
        @media ${theme.star.mediaQuery.tablet} {
            height: 48px;
        }
    `;
};

export const EventRowLoader = withConfig(theme => styled('div', { label: 'EventRowLoader' })<EventsTableLoaderPropsType>`
    padding: 14px 0;
    width: 100%;
    ${({ variant }): SerializedStyles => setEventRowLoader(variant, theme)}
`);

const EventLoader = withConfig(theme => styled(PrimaryLoaderContainer, { label: 'EventLoader' })`
    background-color: ${theme.star.skeletonLoader.placeholder.bgColorNonary};
    height: 8px;
`);

export const FirstLineLoader = withConfig(theme => styled(EventLoader, { label: 'FirstLineLoader' })`
    margin-bottom: 4px;
    width: 101px;
    @media ${theme.star.mediaQuery.tablet} {
        width: 288px;
    }
`);

export const SecondLineLoader = withConfig(theme => styled(EventLoader, { label: 'SecondLineLoader' })`
    width: 163px;
    @media ${theme.star.mediaQuery.tablet} {
        width: 80px;
    }
`);

export const ThirdLineLoader = withConfig(theme => styled(EventLoader, { label: 'ThirdLineLoader' })`
    margin-top: 4px;
    width: 38px;
    @media ${theme.star.mediaQuery.tablet} {
        display: none;
    }
`);

export const FirstLineRacingLoader = withConfig(theme => styled(EventLoader, { label: 'FirstLineRacingLoader' })`
    margin-bottom: 4px;
    width: 120px;
    @media ${theme.star.mediaQuery.tablet} {
        width: 144px;
    }
`);

export const FirstLineRacecardLoader = withConfig(theme => styled(EventLoader, { label: 'FirstLineRacecardLoader' })`
    margin-bottom: 4px;
    width: 120px;
    @media ${theme.star.mediaQuery.tablet} {
        width: 173px;
    }
`);

export const SecondLineRacecardLoader = withConfig(theme => styled(EventLoader, { label: 'SecondLineRacecardLoader' })`
    width: 80px;
    @media ${theme.star.mediaQuery.tablet} {
        width: 115px;
    }
`);

export const RacecardRowLoader = styled('div', { label: 'RacecardRowLoader' })`
    padding: 12px 8px;
    display: flex;
    flex-direction: column;
`;

interface FirstLineRaceInfoPropsType {
    variant: DefaultComponentVariantType | null;
}


export const FirstLineRaceInfo = withConfig(theme => styled(EventLoader, { label: 'FirstLineRaceInfo' })<FirstLineRaceInfoPropsType>`
    height: 14px;
    width: 120px;
    @media ${theme.star.mediaQuery.tablet}{
        height: 20px;
    }
    border-radius: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? theme.star.radius.small : '0'};
`);

interface SecondLineType {
    additional?: boolean;
    variant: DefaultComponentVariantType | null;
}

export const SecondLineRaceInfo = withConfig(theme => styled(EventLoader, { label: 'SecondLineRaceInfo' })<SecondLineType>`
    height: 12px;
    margin-top: 5px;
    width: 164px;
    border-radius: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? theme.star.radius.small : '0'};
    @media ${theme.star.mediaQuery.tablet}{
        ${({ additional }): string => additional === true ? 'display: none' : ''};
    }
`);

export const SecondLineRacingLoader = withConfig(theme => styled(EventLoader, { label: 'SecondLineRacingLoader' })`
    width: 80px;
    @media ${theme.star.mediaQuery.tablet} {
        width: 104px;
    }
`);

interface SelectionLoaderPropsType {
    variant: DefaultComponentVariantType | null;
}

const setSelectionLoader = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            height: 100%;
            border-radius: ${theme.star.radius.small};
            width: 48px;
            @media ${theme.star.mediaQuery.tablet} {
                width: 64px;
            }
        `;
    }
    return css`
        height: 16px;
        margin-left: 24px;
        width: 40px;
    `;
};

export const SelectionLoader = withConfig(theme => styled(PrimaryLoaderContainer, { label: 'SelectionLoader' })<SelectionLoaderPropsType>`
    ${({ variant }): SerializedStyles => setSelectionLoader(variant, theme)}
    background-color: ${theme.star.skeletonLoader.placeholder.bgColorNonary};
`);

export const SelectionSPLoader = withConfig(theme => styled(SelectionLoader, { label: 'SelectionSPLoader' })`
    display: none;
    @media ${theme.star.mediaQuery.tablet} {
        display: block;
    }
`);

export const WeightLoader = withConfig(theme => styled(SelectionLoader, { label: 'WeightLoader' })`
    display: none;
    @media ${theme.star.mediaQuery.tablet} {
        display: block;
    }
`);

const setSelectionsWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            padding: ${theme.star.padding.small};
            gap: ${theme.star.gap.small};
        `;
    }
    return css`
        justify-content: space-between;
        margin-right: 13px;
    `;
};

export const SelectionsWrapper = withConfig(theme => styled('div', { label: 'SelectionsWrapper' })<SelectionLoaderPropsType>`
    ${({ variant }): SerializedStyles => setSelectionsWrapper(variant, theme)}
    align-items: center;
    display: flex;
`);

interface ViewRaceCardLoaderPropsType {
    variant: DefaultComponentVariantType | null;
}

const setViewRaceCardLoader = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            width: 100px;
            border-radius: ${theme.star.radius.small};
        `;
    }
    return css`
        width: 72px;
    `;
};

export const ViewRaceCardLoader = withConfig(theme => styled(PrimaryLoaderContainer, { label: 'ViewRaceCardLoader' })<ViewRaceCardLoaderPropsType>`
    ${({ variant }): SerializedStyles => setViewRaceCardLoader(variant, theme)}
    background-color: ${theme.star.skeletonLoader.placeholder.bgColorNonary};
    height: 14px;
    margin-left: 8px;
    position: relative;
`);

export const SilkLoader = withConfig(theme => styled(SilkLoaderContainer, { label: 'SilkLoader' })`
    background-color: ${theme.star.skeletonLoader.placeholder.bgColorNonary};
    border-radius: 50%;
    height: 24px;
    width: 24px;
`);

export const CasinoGamesHomepage = withConfig(theme => styled('div' , { label: 'CasinoGamesHomepage' })`
    background-color: ${theme.star.skeletonLoader.placeholder.bgColorNonary};
    height: 112px;
    width: 100%;
`);

export const LoadingWrapper = withConfig(theme => styled('div', { label: 'LoadingWrapper' })<WrapperType>`
    ${({ computedHeight }): string => `max-height: ${computedHeight}px`};
    overflow: hidden;
    transition: max-height .2s ease;
    will-change: max-height;
    @media ${theme.star.mediaQuery.desktop}{
        transition: max-height .3s ease;
    }
`);

export const PriceHistory = withConfig(theme => styled(EventLoader, { label: 'PriceHistory' })`
    height: 4px;
    margin-left: 24px;
    width: 44px;
    @media ${theme.star.mediaQuery.tablet} {
        width: 88px;
    }
`);

export const PriceHistoryWrapper = styled('div', { label: 'PriceHistoryWrapper' })`
    display: flex;
    align-items: center;
`;

interface RacecardSecondaryHeaderLoaderPropsType {
    variant: DefaultComponentVariantType | null;
}

export const RacecardSecondaryHeaderLoader = withConfig(theme => styled('div', { label: 'RacecardSecondaryHeaderLoader' })<RacecardSecondaryHeaderLoaderPropsType>`
    background-color: ${theme.star.skeletonLoader.placeholder.bgColorQuinary};
    height: 48px;
    width: 100%;
    border-radius: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? theme.star.radius.small : '0'};
`);

interface RacecardHeaderLoaderPropsType {
    variant: DefaultComponentVariantType | null;
}

const setRacecardHeaderLoader = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            border-radius: ${theme.star.radius.small};
            margin-bottom: 8px;
            height: 32px;
        `;
    }
    return css`
        height: 48px;
    `;
};

export const RacecardHeaderLoader = withConfig(theme => styled('div', { label: 'RacecardHeaderLoader' })<RacecardHeaderLoaderPropsType>`
    ${({ variant }): SerializedStyles => setRacecardHeaderLoader(variant, theme)};
    background-color: ${theme.star.skeletonLoader.placeholder.bgColorSenary};
    width: 100%;
`);

const setRacecardHeaderLoaderWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            margin: 0 8px;
            @media ${theme.star.mediaQuery.tablet} {
                margin: 0;
            }
        `;
    }
    return css``;
};

export const RacecardHeaderLoaderWrapper = withConfig(theme => styled('div', { label: 'RacecardHeaderLoaderWrapper' })<RacecardHeaderLoaderPropsType>`
    ${({ variant }): SerializedStyles => setRacecardHeaderLoaderWrapper(variant, theme)};
`);


export const HorseInfoWrapper = styled('div', { label: 'HorseInfoWrapper' })`
    align-items: center;
    display: flex;
    justify-content: space-between;
`;

interface RaceboxWrapperPropsType {
    variant: DefaultComponentVariantType | null;
}

const setRaceboxWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            border-radius: ${theme.star.radius.medium};
            height: 60px;
            background-color: ${theme.star.skeletonLoader.placeholder.bgColor};
        `;
    }
    return css``;
};

export const RaceboxWrapper = withConfig(theme => styled('div', { label: 'RaceboxWrapper' })<RaceboxWrapperPropsType>`
    ${({ variant }): SerializedStyles => setRaceboxWrapper(variant, theme)};
    align-items: center;
    display: flex;
    justify-content: space-between;
    padding: 8px;
`);

interface EventRowsLoaderWrapperPropsType {
    variant: DefaultComponentVariantType | null;
}

const setEventRowsLoaderWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            display: flex;
            flex-flow: column nowrap;
            gap: ${theme.star.gap.small};
        `;
    }
    return css``;
};

export const EventRowsLoaderWrapper = withConfig(theme => styled('div', { label: 'EventRowsLoaderWrapper' })<EventRowsLoaderWrapperPropsType>`
    ${({ variant }): SerializedStyles => setEventRowsLoaderWrapper(variant, theme)};
`);

interface RacecardSummaryHeaderLoaderPropsType {
    variant: DefaultComponentVariantType | null;
}

const setRacecardSummaryHeaderLoader = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            background-color: ${theme.star.skeletonLoader.placeholder.bgColorQuaternary};
            border-radius: 4px;
        `;
    }
    return css`
        background-color: ${theme.star.skeletonLoader.placeholder.bgColor};
    `;
};

export const RacecardSummaryHeaderLoader = withConfig(theme => styled('div', { label: 'RacecardSummaryHeaderLoader' })<RacecardSummaryHeaderLoaderPropsType>`
    ${({ variant }): SerializedStyles => setRacecardSummaryHeaderLoader(variant, theme)};
    height:16px;
    width: 100%;
    background-color: ${theme.star.skeletonLoader.placeholder.bgColor};
`);

interface RaceCardSummaryLoaderWrapperPropsType {
    variant: DefaultComponentVariantType | null;
}

const setRaceCardSummaryLoaderWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            background: ${theme.star.skeletonLoader.placeholder.bgColorSeptenary};
            display: flex;
            flex-flow: column nowrap;
            gap: ${theme.star.gap.medium};
            padding: ${theme.star.padding.medium};
            @media ${theme.star.mediaQuery.tablet} {
                border-radius: ${theme.star.radius.medium};
            }
        `;
    }
    return css`
        background-color: ${theme.star.skeletonLoader.placeholder.bgColor};
    `;
};

export const RaceCardSummaryLoaderWrapper = withConfig(theme => styled('div', { label: 'RaceCardSummaryLoaderWrapper' })<RaceCardSummaryLoaderWrapperPropsType>`
    ${({ variant }): SerializedStyles => setRaceCardSummaryLoaderWrapper(variant, theme)};
`);

export const FiltersLoader = withConfig(theme => styled('div', { label: 'FiltersLoader' })`
    width: 100%;
    background-color: ${theme.star.skeletonLoader.placeholder.bgColorSeptenary};
    height: 205px;
`);

export const GamesWrapper = withConfig(theme => styled('div', { label: 'GamesWrapper' })`
    padding: 16px;
    align-content: start;
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(auto-fill,minmax(128px,auto));
    flex: 1 1 0%;
    @media ${theme.star.mediaQuery.xMobile} {
        grid-template-columns: repeat(auto-fill,minmax(170px,auto));
    }
    @media ${theme.star.mediaQuery.tablet} {
        grid-template-columns: repeat(auto-fill,minmax(228px,auto));
    }
    @media ${theme.star.mediaQuery.desktop} {
        padding: 24px;
    }
`);

interface GameLoaderPropsType {
    variant: DefaultComponentVariantType | null;
}

export const GameLoader = withConfig(theme => styled(CasinoLoaderContainer, { label: 'GameLoader' })<GameLoaderPropsType>`
    background-color: ${theme.star.skeletonLoader.placeholder.bgColorDenary};
    padding-bottom: 75%;
    position: relative;
    width: 100%;
    border-radius: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? theme.star.radius.medium: '0'};
    @media ${theme.star.mediaQuery.desktop} {
        height: 144px;
    }
`);
